import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useBlocker } from 'react-router-dom'

// ─── Types ────────────────────────────────────────────────────────────────────

type Phase = 'idle' | 'covering' | 'covered' | 'revealing'

interface PageTransitionContextValue {
  phase: Phase
  signalReady: () => void
}

// ─── Timing ───────────────────────────────────────────────────────────────────

const COVER_MS = 220    // how long the fade-to-background takes
const REVEAL_MS = 280   // how long the fade-in of new content takes
const MAX_WAIT_MS = 600 // give up waiting for signalReady and reveal anyway

// ─── Context ──────────────────────────────────────────────────────────────────

const PageTransitionCtx = createContext<PageTransitionContextValue>({
  phase: 'idle',
  signalReady: () => {},
})

export const usePageTransition = () => useContext(PageTransitionCtx)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function PageTransitionProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('idle')

  // Keep a ref of phase so callbacks always see the current value
  const phaseRef = useRef<Phase>('idle')
  useLayoutEffect(() => {
    phaseRef.current = phase
  }, [phase])

  // Track whether the new page has signalled it's ready to show
  const readyRef = useRef(false)

  // Two separate timers: one for the cover→covered delay, one for revealing→idle
  const coverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const revealTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // ── Reveal: fade out the overlay ──────────────────────────────────────────
  const doReveal = useCallback(() => {
    clearTimeout(coverTimer.current)
    setPhase('revealing')
    // Store in a separate ref so the 'covered' effect cleanup can't cancel it
    revealTimer.current = setTimeout(() => setPhase('idle'), REVEAL_MS)
  }, [])

  // ── Called by pages (via usePageReady) when their content is loaded ───────
  const signalReady = useCallback(() => {
    readyRef.current = true
    // If we're already in the 'covered' phase, reveal immediately
    if (phaseRef.current === 'covered') doReveal()
    // Otherwise the useEffect below will catch it when we reach 'covered'
  }, [doReveal])

  // When phase transitions to 'covered': either reveal right away (page
  // already signalled ready) or start the max-wait fallback timer
  useEffect(() => {
    if (phase !== 'covered') return
    if (readyRef.current) {
      doReveal()
      return
    }
    coverTimer.current = setTimeout(doReveal, MAX_WAIT_MS)
    // Only cancel the cover-wait timer; never touch revealTimer
    return () => clearTimeout(coverTimer.current)
  }, [phase, doReveal])

  // ── Navigation blocker ────────────────────────────────────────────────────
  // Intercepts all pathname changes, holds the navigation until the overlay
  // has finished fading in — so the old content stays visible during the fade.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      phaseRef.current === 'idle' &&
      currentLocation.pathname !== nextLocation.pathname
  )

  useEffect(() => {
    if (blocker.state !== 'blocked' || phaseRef.current !== 'idle') return

    readyRef.current = false
    setPhase('covering')
    clearTimeout(coverTimer.current)

    coverTimer.current = setTimeout(() => {
      // Update the ref synchronously so the new page sees 'covered' immediately on mount
      phaseRef.current = 'covered'
      setPhase('covered')
      blocker.proceed()
    }, COVER_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state])

  return (
    <PageTransitionCtx.Provider value={{ phase, signalReady }}>
      {children}
    </PageTransitionCtx.Provider>
  )
}
